import React, { useEffect, useState } from 'react';
import MasterLayout from '../masterLayout/MasterLayout';
import { Form, Button, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import axios from 'axios';
import swal from 'sweetalert';
import { Icon } from '@iconify/react';
import { API_URL } from '..';

const ExpenseLogPage = () => {
  const [formData, setFormData] = useState({
    expense_type: '',
    truck_id: null,
    staff_id: null,
    account_id: null,
    title: '',
    description: '',
    amount: '',
    payment_method: '',
    attachment: null,
    date:null,
  });
  const [options, setOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [user, setUser] = useState(null);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    if (!storedUser) {
      window.location.href = '/sign-in';
    } else {
      setUser(storedUser);
    }
  }, []);

  const expenseTypes = [
    { value: 'truck', label: 'Truck' },
    { value: 'staff', label: 'Staff' },
    { value: 'office_operations', label: 'Office Expense' },
  ];

  const paymentMethods = [
    { value: 'cash', label: 'Cash' },
    { value: 'card', label: 'Card' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
  ];

  const fetchOptions = async (type) => {
    if (!user) return;
    try {
      let response;
      if (type === 'truck') {
        response = await axios.get(`${API_URL}/api/truck/${user.id}`, {
          headers: { 'Content-Type': 'application/json', Accept: '*/*' },
        });
        setOptions(
          response.data.truck.map((truck) => ({
            value: truck.id,
            label: truck.name,
          }))
        );
      } else if (type === 'staff') {
        response = await axios.get(`${API_URL}/api/employee/fetch/${user.id}`, {
          headers: { 'Content-Type': 'application/json', Accept: '*/*' },
        });
        setOptions(
          response.data.employees.map((staff) => ({
            value: staff.id,
            label: staff?.first_name + ' ' + staff?.last_name,
          }))
        );
      } else if (type === 'office_operations') {
        response = await axios.get(`${API_URL}/api/accounts/${user.id}`, {
          headers: { 'Content-Type': 'application/json', Accept: '*/*' },
        });
        setOptions(
          response.data.accounts.map((account) => ({
            value: account.id,
            label: account.title,
          }))
        );
      }
    } catch (error) {
      swal('Error', `Failed to fetch ${type} options: ${error.message}`, 'error');
    }
  };

  const handleExpenseTypeChange = (e) => {
    const type = e.target.value;
    setFormData({
      ...formData,
      expense_type: type,
      truck_id: null,
      staff_id: null,
      account_id: null,
    });
    setOptions([]);
    setErrors({ ...errors, expense_type: '', conditional_id: '' });
    if (type) {
      fetchOptions(type);
    }
  };

  const handleSelectChange = (selectedOption) => {
    setFormData({
      ...formData,
      truck_id: formData.expense_type === 'truck' ? selectedOption?.value : null,
      staff_id: formData.expense_type === 'staff' ? selectedOption?.value : null,
      account_id: formData.expense_type === 'office_operations' ? selectedOption?.value : null,
    });
    setErrors({ ...errors, conditional_id: '' });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, attachment: file });
    setErrors({ ...errors, attachment: '' });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.expense_type) newErrors.expense_type = 'Expense type is required';
    if (
      formData.expense_type &&
      !formData.truck_id &&
      !formData.staff_id &&
      !formData.account_id
    ) {
      newErrors.conditional_id = `Please select a ${
        formData.expense_type === 'truck'
          ? 'truck'
          : formData.expense_type === 'staff'
          ? 'staff'
          : 'account'
      }`;
    }
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.amount || formData.amount <= 0) newErrors.amount = 'Valid amount is required';
    if (!formData.payment_method) newErrors.payment_method = 'Payment method is required';
    if (!formData.date) newErrors.date = 'Date is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    const user = JSON.parse(localStorage.getItem('user'));
    const data = new FormData();
    data.append('expense_type', formData.expense_type);
    data.append('affiliate_id', user.id);
    if (formData.truck_id) data.append('expense_applied_on', formData.truck_id);
    if (formData.staff_id) data.append('expense_applied_on', formData.staff_id);
    if (formData.account_id) data.append('expense_applied_on', formData.account_id);
    data.append('title', formData.title);
    data.append('description', formData.description);
    data.append('amount', formData.amount);
    data.append('on_date', formData.date);
    data.append('payment_method', formData.payment_method);
    if (formData.attachment) data.append('attachment', formData.attachment);

    try {
      const response = await axios.post(`${API_URL}/api/expense/create`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Accept: '*/*',
        },
      });

      if (response.status === 201) {
        swal('Success', 'Expense logged successfully', 'success');
        setFormData({
          expense_type: '',
          truck_id: null,
          staff_id: null,
          account_id: null,
          title: '',
          description: '',
          amount: '',
          payment_method: '',
          attachment: null,
        });
        setOptions([]);
        setErrors({});
      } else {
        swal('Error', response.data.message || 'Failed to log expense', 'error');
      }
    } catch (error) {
      swal('Error', 'Failed to log expense: ' + error.message, 'error');
    }
  };

  return (
    <MasterLayout>
      <div className="container py-5">
        <Row className="justify-content-center">
          <Col lg={8}>
            <div className="card shadow-sm border-0">
              <div className="card-header bg-white border-bottom">
                <h6 className="h6 mb-0">Log Expense</h6>
              </div>
              <div className="card-body">
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3" controlId="expenseType">
                    <Form.Label className="fw-bold">Expense Type</Form.Label>
                    <Form.Select
                      name="expense_type"
                      value={formData.expense_type}
                      onChange={handleExpenseTypeChange}
                      isInvalid={!!errors.expense_type}
                    >
                      <option value="">Select Expense Type</option>
                      {expenseTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.expense_type}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {formData.expense_type && (
                    <Form.Group className="mb-3" controlId="conditionalSelect">
                      <Form.Label className="fw-bold">
                        {formData.expense_type === 'truck'
                          ? 'Select Truck'
                          : formData.expense_type === 'staff'
                          ? 'Select Staff'
                          : 'Select Account'}
                      </Form.Label>
                      <Select
                        options={options}
                        value={options.find(
                          (opt) =>
                            opt.value ===
                            (formData.truck_id || formData.staff_id || formData.account_id)
                        )}
                        onChange={handleSelectChange}
                        placeholder={`Select ${
                          formData.expense_type === 'truck'
                            ? 'Truck'
                            : formData.expense_type === 'staff'
                            ? 'Staff'
                            : 'Account'
                        }`}
                        isClearable
                      />
                      {errors.conditional_id && (
                        <div className="invalid-feedback d-block">
                          {errors.conditional_id}
                        </div>
                      )}
                    </Form.Group>
                  )}
                  {
                    formData.expense_type === 'truck' && (
                        <p><strong style={{color:'red'}}>Note</strong>: This expense will visible only in truck details expense tab.</p>
                    )
                  }
                  <Form.Group className="mb-3" controlId="title">
                    <Form.Label className="fw-bold">Expense Title</Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Enter expense title"
                      isInvalid={!!errors.title}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.title}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="description">
                    <Form.Label className="fw-bold">Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Enter description (optional)"
                    />
                  </Form.Group>

                <div className='row'>
                <Form.Group className="col-md-6 mb-3" controlId="amount">
                    <Form.Label className="fw-bold">Amount</Form.Label>
                    <Form.Control
                      type="number"
                      name="amount"
                      value={formData.amount}
                      onChange={handleInputChange}
                      placeholder="Enter amount"
                      min="0"
                      step="0.01"
                      isInvalid={!!errors.amount}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.amount}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className="col-md-6 mb-3" controlId="date">
                    <Form.Label className="fw-bold">Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      isInvalid={!!errors.date}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.date}
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>

                  <Form.Group className="mb-3" controlId="paymentMethod">
                    <Form.Label className="fw-bold">Payment Method</Form.Label>
                    <Form.Select
                      name="payment_method"
                      value={formData.payment_method}
                      onChange={handleInputChange}
                      isInvalid={!!errors.payment_method}
                    >
                      <option value="">Select Payment Method</option>
                      {paymentMethods.map((method) => (
                        <option key={method.value} value={method.value}>
                          {method.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.payment_method}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="attachment">
                    <Form.Label className="fw-bold">Attachment (Optional)</Form.Label>
                    <Form.Control
                      type="file"
                      name="attachment"
                      onChange={handleFileChange}
                      accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                    />
                  </Form.Group>

                  <div className="text-end">
                    <Button variant="primary" type="submit">
                      <Icon icon="simple-line-icons:check" className="me-2" />
                      Submit
                    </Button>
                  </div>
                </Form>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </MasterLayout>
  );
};

export default ExpenseLogPage;